import { getThemeClass } from "~/styles/styles"
import Section from "../../common/section"
import FeatureElement, { type FeatureElementProps, type FeatureElementVariant } from "."
import Image, { type StaticImageData } from "next/image"
import { type FeatureItemProps } from "./featureItem"

export type FeatureSectionVariant =
  // text on left on top, 2-3 columns of features left-aligned below
  | "left"
  // text centred on top, 2-3 columns of features centred below
  | "center"
  // text on left, 2 columns of features on right
  | "offset"
  //  text on right on top 2-3 columns of features left-aligned below
  | "right"

export type FeatureGridProps<Variant extends FeatureElementVariant> = {
  theme?: string
  bgClass?: string
  sectionVariant?: FeatureSectionVariant // default left 3

  title?: string | JSX.Element
  titlesm?: boolean
  titleColour?: string
  body?: string | string[] | JSX.Element
  bodyClass?: string

  itemVariant?: Variant
  items: FeatureElementProps<Variant>[]

  gridCols?: string
  gridGaps?: string
  afterAction?: string | JSX.Element

  padding?: "sm" | "md"
  xPadding?: string
  flexHeight?: boolean // section height
  customBorder?: string
  divClass?: string

  maxWidth?: string
  textMaxWOverride?: string

  right?: boolean
  gridClass?: string

  // heart icon image
  iconImage?: string | StaticImageData
  iconImgLeft?: boolean
  iconImgPosition?: string
  iconImageClass?: string
  hideImageOnMobile?: boolean
}

export default function FeatureGrid<Variant extends FeatureElementVariant>({ bgClass, right, ...props }: FeatureGridProps<Variant>) {
  const titleClass = props.titlesm
    ? `font-bold text-desk-resp-3xl ${props.theme === "light" ? "text-purpleblue" : props.titleColour}`
    : `font-bold text-resp-4xl ${props.theme === "light" ? "text-purpleblue" : props.titleColour}`

  const body = Array.isArray(props.body)
    ? props.body.map((line, i) => (
        <p key={i} className="mt-1 md:mt-2">
          {line}
        </p>
      ))
    : props.body

  const bodyColour = `${props.theme === "light" ? "text-grey-85" : "text-white"}`

  const padding = props.padding === "sm" ? "py-14 sm:py-16 lg:py-20" : props.padding === "md" ? "py-20 sm:py-24 md:py-36" : "py-28 sm:py-32 md:py-44 xl:py-48"

  const gridCols = props.gridCols ?? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
  const gridGaps = props.gridGaps ?? "gap-x-6 gap-y-8"

  const itemVariant = props.itemVariant ?? "large-image-contain"
  const sectionVariant = props.sectionVariant ?? "left"

  const iconImage = props.iconImage && (
    <Image
      src={props.iconImage}
      alt="heart-image"
      className={`absolute bottom-0 w-72 md:w-96 lg:w-[28rem] xl:w-[32rem] ${props.iconImageClass ?? ""} ${props.iconImgLeft ? (props.iconImgPosition ?? `-left-12 right-0 lg:-left-16 xl:-left-24`) : (props.iconImgPosition ?? `-right-14 md:-right-16 xl:-right-24`)} ${props.hideImageOnMobile ? "hidden xs:block" : ""}`}
    />
  )

  const layout =
    sectionVariant === "left" ? (
      <>
        <div
          className={`relative mx-auto flex flex-col ${props.divClass ?? "justify-center"} leading-8 ${props.xPadding ?? "px-8 md:px-12 xl:px-16 2xl:px-10"} ${padding}`}
        >
          <div className={`${props.textMaxWOverride ?? "max-w-2xl"}`}>
            {typeof props.title === "string" ? (
              <h2 className={`mb-10 xl:mb-12 ${titleClass}`}>{props.title}</h2>
            ) : (
              props.title && <div className={`${titleClass} ${props.titlesm ? "mb-2" : "mb-8"}`}>{props.title}</div>
            )}
            {body && <div className={`text-desk-resp-xl ${bodyColour} ${props.bodyClass}`}>{body}</div>}
          </div>
          {props.items && (
            <div className={`z-10 flex flex-col w-full`}>
              <div className={`mx-auto w-full grid ${gridCols} ${gridGaps}`}>
                {props.items.map((item, i) => (
                  <FeatureElement key={i} {...item} variant={itemVariant} bodyColour={(item as FeatureItemProps).bodyColour ?? bodyColour} />
                ))}
              </div>
            </div>
          )}

          {typeof props.afterAction === "string" ? (
            <div className={`mt-8 text-center text-lg font-bold text-orange sm:mt-10 ml:text-xl lg:mt-14 xl:text-2xl z-10`}>{props.afterAction}</div>
          ) : (
            props.afterAction && <div className="z-10">{props.afterAction}</div>
          )}

          {iconImage}
        </div>
      </>
    ) : sectionVariant === "center" ? (
      <>
        <div
          className={`relative mx-auto flex flex-col items-center justify-center text-center ${props.divClass ?? "justify-center"} leading-8 ${props.xPadding ?? "px-8 md:px-12 xl:px-16"} ${padding} `}
        >
          <div className={`${props.textMaxWOverride ?? "max-w-2xl"}`}>
            {typeof props.title === "string" ? (
              <h2 className={`mb-6 xl:mb-8 ${titleClass}`}>{props.title}</h2>
            ) : (
              props.title && <div className={`${titleClass} mb-6`}>{props.title}</div>
            )}
            {body && <div className={`text-desk-resp-xl ${bodyColour} ${props.bodyClass}`}>{body}</div>}
          </div>

          {props.items && (
            <div className={`z-10 flex flex-col text-left w-full`}>
              <div className={`mx-auto w-full grid ${gridCols} ${gridGaps}`}>
                {props.items.map((item, i) => (
                  <FeatureElement key={i} {...item} variant={itemVariant} bodyColour={(item as FeatureItemProps).bodyColour ?? bodyColour} />
                ))}
              </div>
            </div>
          )}

          {typeof props.afterAction === "string" ? (
            <div className={`mt-8 text-center text-lg font-bold text-orange sm:mt-10 ml:text-xl lg:mt-14 xl:text-2xl`}>{props.afterAction}</div>
          ) : (
            props.afterAction && <div>{props.afterAction}</div>
          )}

          {iconImage}
        </div>
      </>
    ) : sectionVariant === "offset" ? (
      <>
        <div
          className={`relative grid grid-cols-1 gap-6 lg:grid-cols-3 ${props.divClass ?? "justify-center"} leading-8 ${props.xPadding ?? "px-8 md:px-12 xl:px-16"} ${padding}`}
        >
          <div className={`${props.textMaxWOverride ?? "max-w-2xl"}`}>
            {typeof props.title === "string" ? (
              <h2 className={`mb-6 xl:mb-10 ${titleClass}`}>{props.title}</h2>
            ) : (
              props.title && <div className={`${titleClass} mb-6 xl:mb-10`}>{props.title}</div>
            )}
            {body && <div className={`text-desk-resp-xl leading-6 ${bodyColour} ${props.bodyClass}`}>{body}</div>}
          </div>

          {props.items && (
            <div className={`z-10 col-span-2 flex flex-col w-full`}>
              <div className={`mx-auto w-full grid ${gridCols} ${gridGaps}`}>
                {props.items.map((item, i) => (
                  <FeatureElement key={i} {...item} variant={itemVariant} bodyColour={(item as FeatureItemProps).bodyColour ?? bodyColour} />
                ))}
              </div>
            </div>
          )}

          {typeof props.afterAction === "string" ? (
            <div className={`mt-8 text-center text-lg font-bold text-orange sm:mt-10 ml:text-xl lg:mt-14 xl:text-2xl`}>{props.afterAction}</div>
          ) : (
            props.afterAction && <div>{props.afterAction}</div>
          )}

          {iconImage}
        </div>
      </>
    ) : sectionVariant === "right" ? (
      <>
        <div
          className={`relative mx-auto flex flex-col ${props.divClass ?? "justify-center"} leading-8 ${props.xPadding ?? "px-8 md:px-12 xl:px-16"} ${padding}`}
        >
          <div className={`${props.textMaxWOverride ?? "max-w-2xl"} ml-auto text-right`}>
            {typeof props.title === "string" ? (
              <h2 className={`mb-10 xl:mb-12 ${titleClass}`}>{props.title}</h2>
            ) : (
              props.title && <div className={`${titleClass} ${props.titlesm ? "mb-2" : "mb-8"}`}>{props.title}</div>
            )}
            {body && <div className={`text-desk-resp-xl ${bodyColour} ${props.bodyClass}`}>{body}</div>}
          </div>
          {props.items && (
            <div className={`z-10 flex flex-col w-full`}>
              <div className={`mx-auto w-full grid ${gridCols} ${gridGaps}`}>
                {props.items.map((item, i) => (
                  <FeatureElement key={i} {...item} variant={itemVariant} bodyColour={(item as FeatureItemProps).bodyColour ?? bodyColour} />
                ))}
              </div>
            </div>
          )}

          {typeof props.afterAction === "string" ? (
            <div className={`mt-8 text-center text-lg font-bold text-orange sm:mt-10 ml:text-xl lg:mt-14 xl:text-2xl`}>{props.afterAction}</div>
          ) : (
            props.afterAction && <div>{props.afterAction}</div>
          )}

          {iconImage}
        </div>
      </>
    ) : (
      <section />
    )

  return (
    <Section
      flexHeight={props.flexHeight}
      className={`relative flex overflow-hidden ${bgClass ?? ""} ${props.theme ? getThemeClass(props.theme, props.customBorder ?? undefined) : getThemeClass("dark")}`}
    >
      <div className={`relative mx-auto flex w-full ${props.maxWidth ?? "max-w-7xl"}`}>{layout}</div>
    </Section>
  )
}
